import * as React from 'react';
import { Global, css } from '@emotion/core';
import { Text } from '@lightspeed/flame/Text';

import withLocalizeAwareIframe from 'pages/utils/withLocalizeAwareIframe';
import withRouteAwareIframe from 'pages/utils/withRouteAwareIframe';
import withValidSessionIframe from 'pages/utils/withValidSessionIframe';
import { EMPLOYEE_ROLES } from './types';
import { paths as routePaths } from './routes';

import NoRights from '../../generic/NoRights';

// Offers previously sent as /payments/lightspeed-capital/offer/:offer_id: shouldn't be restricted
export const hasEmailOffer = (): boolean => window.location.pathname.split('/')[3] === 'offer';

const iframeStyle = { border: 'none', width: '100%', height: '100%', display: 'block' };

const LightspeedCapitalIframe: React.FC = () => {
  const { pathname, search } = window.location;

  // Restrict access to either the email offer page or /offers for anything else
  const showEmailOffer = hasEmailOffer();
  const landingPage = showEmailOffer ? pathname : `${routePaths.lightspeedCapital}/offers`;

  const urlParams = [
    'app=payments-capital',
    `next=${landingPage}${encodeURIComponent(search)}`,
  ].join('&');

  const [isLoading, setIsLoading] = React.useState(true);
  const [hasAccess, setHasAccess] = React.useState(false);

  React.useEffect(() => {
    window.merchantos.asyncSubscribe.notifySubscribers('react.mount.topbar', {
      props: {
        crumbs: [
          {
            type: 'ui_tab',
            text: 'Settings',
            name: 'admin',
          },
          {
            text: 'Capital',
          },
        ],
      },
    });

    // Restriction does not apply to email offers
    if (showEmailOffer) {
      setHasAccess(true);
      setIsLoading(false);
      return;
    }

    // @ts-expect-error ts-migrate(2339) FIXME: Property 'employeeID' does not exist on type 'Merc... Remove this comment to see the full error message
    const { employeeID } = window.merchantos.session.sessData.Employee;

    window.merchantos.session
      .apiRequest(`/API/Account/{{systemCustomerID}}/Employee/${employeeID}.json`)
      .then(data => {
        const { employeeRoleID } = data.Employee;
        const { ADMIN, OWNER } = EMPLOYEE_ROLES;
        const canHaveAccess = employeeRoleID === OWNER || employeeRoleID === ADMIN;
        setHasAccess(canHaveAccess);
        setIsLoading(false);
      })
      .catch((_jqXHR, _textStatus, errorThrown) => {
        window.newrelic?.noticeError(errorThrown);
        setIsLoading(false);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading) {
    return <iframe title="Lightspeed Capital - Loading" style={iframeStyle} />;
  }

  if (!hasAccess) {
    return (
      <>
        <NoRights customText={<Text>You do not have permission to access this page.</Text>} />
        <iframe title="Lightspeed Capital - Access restricted" style={iframeStyle} />
      </>
    );
  }

  return (
    <React.Fragment>
      {showEmailOffer && (
        <Global
          styles={css`
            .react-page {
              position: absolute;
              top: 0px;
              left: 0px;
              width: 100%;
              height: 100%;
              z-index: 1000;
            }
          `}
        />
      )}
      <iframe
        title="Lightspeed Capital"
        src={`/webapps_redirect.php?${urlParams}`}
        style={iframeStyle}
      />
    </React.Fragment>
  );
};

export default withLocalizeAwareIframe(
  withRouteAwareIframe(withValidSessionIframe(LightspeedCapitalIframe))
);
